import React from 'react';
import {
  Box,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Container,
  Stack,
  Paper,
} from '@mui/material';
import { 
  Star as StarIcon, 
  Lock as LockIcon,
  WorkspacePremium as PremiumIcon,
  Chat as ChatIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAppTheme } from '../App';

interface SubscriptionGateProps {
  children: React.ReactNode;
  feature: string;
  featureName: string;
  description?: string;
  showUpgradeCard?: boolean;
}

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({
  children,
  feature,
  featureName,
  description,
  showUpgradeCard = true,
}) => {
  const { hasFeature, isPro, loading } = useSubscription();
  const { getThemeStyles, isDarkMode } = useAppTheme();
  const theme = getThemeStyles();
  const navigate = useNavigate();
  const { trackSubscriptionEvent, trackCustomEvent } = useAnalytics();

  // Handle upgrade button click with analytics tracking
  const handleUpgradeClick = () => {
    // Track subscription gate upgrade click
    trackSubscriptionEvent('view_plans');
    trackCustomEvent('subscription_gate_upgrade_click', {
      feature: feature,
      feature_name: featureName,
      source: 'subscription_gate',
      context: 'blocked_feature_access'
    });
    
    // Navigate to subscription page
    navigate('/subscription');
  };

  // Show loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ backgroundColor: theme.background }}
      >
        <Typography color={theme.textSecondary} variant="h6">
          Loading subscription status...
        </Typography>
      </Box>
    );
  }

  // If user has access, render children
  if (hasFeature(feature) || isPro) {
    return <>{children}</>;
  }

  // If showUpgradeCard is false, just show a simple message
  if (!showUpgradeCard) {
    return (
      <Alert
        severity="info"
        icon={<LockIcon />}
        sx={{
          backgroundColor: isDarkMode 
            ? 'rgba(254, 106, 46, 0.15)' 
            : 'rgba(254, 106, 46, 0.08)',
          color: '#FE6A2E',
          border: `1px solid ${isDarkMode ? 'rgba(254, 106, 46, 0.3)' : 'rgba(254, 106, 46, 0.2)'}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          {featureName} is a Pro feature. Upgrade to access this functionality.
        </Typography>
      </Alert>
    );
  }

  // Show professional upgrade screen
  return (
    <Box
      sx={{
        backgroundColor: theme.background,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={isDarkMode ? 12 : 8}
          sx={{
            backgroundColor: theme.cardBackground,
            borderRadius: 4,
            overflow: 'hidden',
            border: `1px solid ${theme.borderColor}`,
            position: 'relative',
          }}
        >
          {/* Premium Badge */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #FE6A2E 0%, #FF8A50 100%)',
              py: 1,
              textAlign: 'center',
            }}
          >
            <Chip
              icon={<PremiumIcon sx={{ color: 'white !important' }} />}
              label="Pro Feature"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            />
          </Box>

          <CardContent sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
            {/* Icon */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(254, 106, 46, 0.2) 0%, rgba(255, 138, 80, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: `2px solid ${isDarkMode ? 'rgba(254, 106, 46, 0.3)' : 'rgba(254, 106, 46, 0.2)'}`,
                }}
              >
                <ChatIcon sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3rem' }, 
                  color: '#FE6A2E',
                }} />
              </Box>
            </Box>

            {/* Title */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: theme.textPrimary,
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Unlock {featureName}
            </Typography>

            {/* Description */}
            <Typography
              variant="h6"
              sx={{
                color: theme.textSecondary,
                mb: 4,
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 400,
                lineHeight: 1.6,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              {description || `${featureName} is available with our Pro plan. Get unlimited access to our AI-powered visa assistant and advanced features.`}
            </Typography>

            {/* Features Preview */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="center" 
              alignItems="center"
              sx={{ mb: 4 }}
            >
              {[
                { icon: <ChatIcon />, text: 'Unlimited Chat' },
                { icon: <RocketIcon />, text: 'Priority Support' },
                { icon: <StarIcon />, text: 'Advanced Features' },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: isDarkMode 
                      ? 'rgba(254, 106, 46, 0.1)' 
                      : 'rgba(254, 106, 46, 0.05)',
                    border: `1px solid ${isDarkMode ? 'rgba(254, 106, 46, 0.2)' : 'rgba(254, 106, 46, 0.1)'}`,
                  }}
                >
                  <Box sx={{ color: '#FE6A2E', fontSize: '1.25rem' }}>
                    {item.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.textPrimary,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Pricing */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
                <Typography
                  variant="h2"
                  component="span"
                  sx={{
                    color: '#FE6A2E',
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', sm: '3rem' },
                    lineHeight: 1,
                  }}
                >
                  $10
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.textSecondary,
                    fontWeight: 500,
                  }}
                >
                  /week
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  color: theme.textSecondary,
                  mt: 0.5,
                  fontSize: '0.875rem',
                }}
              >
                Cancel anytime
              </Typography>
            </Box>

            {/* CTA Button */}
            <Button
              variant="contained"
              size="large"
              onClick={handleUpgradeClick}
              sx={{
                backgroundColor: '#FE6A2E',
                color: 'white',
                fontWeight: 700,
                py: { xs: 2, sm: 2.5 },
                px: { xs: 4, sm: 6 },
                borderRadius: 3,
                textTransform: 'none',
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                boxShadow: '0 8px 32px rgba(254, 106, 46, 0.3)',
                minWidth: { xs: '100%', sm: 280 },
                '&:hover': {
                  backgroundColor: '#E5511A',
                  boxShadow: '0 12px 40px rgba(254, 106, 46, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Upgrade to Pro
            </Button>

            {/* Secondary Text */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 3,
                color: theme.textSecondary,
                fontSize: '0.75rem',
              }}
            >
              Join thousands of successful visa applicants
            </Typography>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

export default SubscriptionGate; 